import type { ValueEvent } from '@adornis/base/utilTypes';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import { acss, ASSDirective } from '@adornis/chemistry/directives/acss';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/digitale-helden-shared/client/components/component-icon-button.js';
import '@adornis/digitale-helden-shared/client/theme/d-checkbox';
import '@adornis/digitale-helden-shared/client/theme/d-input';
import { DigitaleHeldenFont } from '@adornis/digitale-helden-shared/client/theme/fonts';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { BaseMarketingLink } from '../../db/links/BaseMarketingLink';

export const MarketingLinkExtension: BuildifyExtension<BaseMarketingLink> = {
  class: BaseMarketingLink,
  toolbar: {
    icon: 'link',
    text: {
      default: 'Navigations Link',
    },
  },
  group: 'Marketing',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-link
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-link>
    `;
  },
  editor: ({ content, contentController, controllerBaseKeyPath, host }) => {
    return html`
      <d-flex space="md">
        <d-input placeholder="Name" ${contentController.field(...controllerBaseKeyPath, 'name')}></d-input>
        <d-input placeholder="Link" ${contentController.field(...controllerBaseKeyPath, 'href')}></d-input>
        <d-checkbox
          .label=${'In neuem Fenster öffnen?'}
          .value=${content.target === '_blank'}
          @value-picked=${(e: ValueEvent<boolean>) => {
            const target = e.detail.value ? '_blank' : '_self';
            contentController.pickValueForField([...controllerBaseKeyPath, 'target'], target);
          }}
        ></d-checkbox>
      </d-flex>
    `;
  },
};

@customElement('marketing-link')
export class MarketingLink extends ExtensionBoilerplate<BaseMarketingLink> {
  override _content(content: BaseMarketingLink) {
    const isActive = window.location.pathname === content.href;
    return html`
      <d-link
        href=${isActive ? '' : content.href ?? ''}
        target=${content.target ?? '_self'}
        ${acss(this._linkStyling(isActive))}
      >
        <d-text ${css({ fontFamily: 'inherit', textDecoration: 'inherit' })}> ${content.name ?? 'Linkname'} </d-text>
      </d-link>
    `;
  }

  protected _linkStyling(isActiveLink: boolean): import('lit/async-directive.js').DirectiveResult<typeof ASSDirective> {
    return {
      fontFamily: isActiveLink ? DigitaleHeldenFont.BOLD : DigitaleHeldenFont.REGULAR,
      '&:hover': {
        color: 'unset',
        textDecoration: isActiveLink ? 'unset' : 'underline',
      },
    };
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {},
      },
    ];
  }
}
